import { useMutation } from '@tanstack/react-query'
import { useEffect, useState, useContext } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import axiosInstance from '../../api/axiosInstance'
import FormElementCard from '../../components/create-form/FormElementCard'
import { Button } from '../../components/ui/button'
import { Form, FormControl, FormField, FormItem } from '../../components/ui/form'
import { useCreateData } from '../../hooks/useApi'
import useTitle from '../../hooks/useTitle'
import logger from '../../utils/logger'
import NotFound from '../not-found'
import { AuthContext } from '../../contexts/auth-context'
import AuthModal from '../../components/modals/auth-modal'
import { useNavigate } from 'react-router-dom'

export default function GeneratedForm({ sharedId }) {
  const [formData, setFormData] = useState(null)
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { id } = useParams()

  const { setIsLoginModalOpen, setModalContent, setActiveTab, setAccountAlreadyCreated, auth } =
    useContext(AuthContext)

  const mutation = useMutation({
    mutationFn: (response) =>
      axiosInstance.post('/business-register-form', {
        businessId: id,
        formId: formData?._id,
        ...response,
      }),
    onSuccess: ({ data }) => {
      toast.success(data?.message)
      setIsSubmitting(false)
      form.reset() // <-- Clear form data here
      // setFormData(null)
      setTimeout(() => {
        if (!auth.authenticate) {
          setIsLoginModalOpen(true)
          setModalContent(<AuthModal />)
          setActiveTab('user')
          setAccountAlreadyCreated(true)
        } else {
          navigate('/user/profile')
        }
      }, 2000)
    },
    onError: (error) => {
      logger.error(error?.response?.data?.message)
      toast.error(error?.response?.data?.message)
      setIsSubmitting(false)

      if (error?.response?.data?.code === 'MOBILE_AND_EMAIL_EXISTS') {
        setTimeout(() => {
          setIsLoginModalOpen(true)
          setModalContent(<AuthModal />)
          setActiveTab('user')
          setAccountAlreadyCreated(true)
        }, 1000)
      }
    },
  })

  const { mutate, isPending, error } = useCreateData('/getActiveForm', 'forms')

  useEffect(() => {
    mutate(
      { businessId: sharedId || id },
      {
        onSuccess: (responseData) => {
          const targetForm = Array.isArray(responseData?.data?.form)
            ? responseData.data.form.find((form) => form._id === id) || responseData.data.form[0]
            : responseData?.data?.form

          setFormData(targetForm)
        },
        onError: (err) => {
          logger.error('🔴 Error creating data:', err)
          toast.error('Failed to load form')
        },
      }
    )
  }, [sharedId, id, mutate])

  useTitle(formData?.name || 'Form Builder')

  const form = useForm()

  useEffect(() => {
    if (!formData) return

    const defaultValues = {}

    formData.elements.forEach(({ id, type }) => {
      if (['switch', 'checkbox'].includes(type)) {
        defaultValues[id] = false
      } else if (type === 'checklist') {
        defaultValues[id] = []
      } else if (type === 'mobile_number') {
        defaultValues[id] = {
          countryCode: '+971',
          number: '',
          formatted: '+971 ',
        }
      } else {
        defaultValues[id] = null
      }
    })

    form.reset(defaultValues)
  }, [formData, form])

  const onSubmit = (values) => {
    if (!formData) return

    for (const { id, required, type } of formData.elements) {
      if (
        required &&
        (!values[id] ||
          (type === 'checklist' && values[id].length === 0) ||
          (type === 'mobile-number' && !values[id]?.number))
      ) {
        toast.error('Please fill all required fields')
        return
      }
    }

    setIsSubmitting(true) // Activate loading state

    const structuredUserData = {}
    const otherData = {}
    const topLevelKeys = ['name', 'email', 'mobile_number']

    formData.elements.forEach(({ id, label, type, options }) => {
      if (['heading', 'description'].includes(type)) return

      let answer

      if (type === 'mobile-number') {
        answer = values[id]?.formatted || null
      } else if (options && type !== 'checklist') {
        const selected = options.find(({ value }) => value === values[id])
        answer = selected?.label || null
      } else {
        answer = values[id] ?? null
      }

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = label
      const plainTextLabel = tempDiv.textContent || tempDiv.innerText || ''

      const key = plainTextLabel.toLowerCase().trim().replace(/\s+/g, '_')

      if (topLevelKeys.includes(key)) {
        structuredUserData[key] = answer
      } else {
        otherData[key] = answer
      }
    })

    structuredUserData.other_data = otherData
    structuredUserData.formId = formData?._id
    mutation.mutate(structuredUserData)
  }

  if (isPending) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Loading form...</div>
      </div>
    )
  }

  if (error || !formData) {
    return <NotFound fullScreen={false} />
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="relative flex flex-col w-full">
        {isSubmitting && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white bg-opacity-60 space-y-3">
            <div className="w-12 h-12 border-4 border-heading-primary border-t-transparent rounded-full animate-spin" />
            <div className="text-sm font-medium text-heading-primary">Submitting...</div>
          </div>
        )}
        {/* Header */}
        <header className="top-0 z-30 w-full border-b shadow bg-white rounded-md">
          <div className="max-w-7xl mx-auto flex h-24 items-center justify-between px-6">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <div className="h-16 w-16 overflow-hidden rounded">
                  <img
                    src={`${import.meta.env.VITE_BASE_URL}${formData.logoUrl}`}
                    alt="Company Logo"
                    className="h-full w-full object-contain"
                  />
                </div>
              </Link>
              <h1 className="text-[22px] font-semibold text-heading-primary">
                {formData.name || 'Registration Form'}
              </h1>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex w-full">
          <div className="w-full py-3">
            <ul className="space-y-1">
              {formData.elements &&
                formData.elements
                  .filter((element) => !['heading', 'description'].includes(element.type))
                  .map((element) => (
                    <li key={element.id}>
                      <FormField
                        control={form.control}
                        name={element.id}
                        render={({ field }) => (
                          <FormItem className="gap-5">
                            <FormControl>
                              <FormElementCard formElement={element} isView field={field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </li>
                  ))}

              {!formData.isActive && (
                <li className="text-sm font-medium text-rose-700">
                  * Note: The status of this form is closed.
                </li>
              )}

              <li>
                <Button
                  className="bg-heading-primary rounded-sm text-lg mt-5 px-3 py-6 cursor-pointer hover:bg-heading-primary-400 w-full"
                  disabled={!formData.isActive || isSubmitting}
                  type="submit"
                >
                  Submit
                </Button>
              </li>
            </ul>
          </div>
        </div>
      </form>
    </Form>
  )
}
